import {List, Map} from 'immutable';
import {
  emptyObject,
  emptyArray,
  flatMap,
  identity,
} from '@packages/utilities/fp/utils';
import {map as mapValues, assoc, dissoc} from '@packages/utilities/key-map';
import {
  type Type,
  EnvTypes,
  Variable,
  Expressions,
  withSystem,
  withMeta,
} from '@packages/systems/wfdl';
import {
  type Expression,
  EHole,
  EFunction,
  ECall,
  EVariable,
  EList,
  EUnion,
  ERecord,
  ESelect,
  ECase,
  EText,
  TRecord,
  TEmptyRecord,
  TText,
  TNumber,
  TList,
  TUnion,
  TTypeConstructor,
  TTypeReference,
  functionOf,
  TTypeApplication,
} from '@packages/systems/wfdl-ast';
import {
  forall,
  call1,
  call2,
  func,
  $,
  _$,
  tyfun,
} from '@packages/utilities/wfdl-helpers';
import {
  TEffect,
  bind,
  pure,
  fail,
  lookup,
  flow,
} from '@packages/systems/plugins/Effect';
import {TJson} from '@packages/systems/plugins/Json';
import {
  expectEmailRef,
  expectLinkRef,
  expectVideoLinkRef,
  expectColorRef,
  initializeField,
  adjustFieldValue,
  supportedFieldsBySlug,
  createSearchByType,
} from '@packages/systems/plugins/CMS';
import {
  TNonEmptyText,
  concat,
  textEquals,
} from '@packages/systems/plugins/Text';
import {TOptionalNumber} from '@packages/systems/plugins/Number';
import {TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {
  foldRight,
  prepend,
  map as listMap,
} from '@packages/systems/plugins/List';
import {TEmail, TOptionalEmail} from '@packages/systems/plugins/Email';
import {TPhone, TNonEmptyPhone} from '@packages/systems/plugins/Phone';
import {TLink, TOptionalLink} from '@packages/systems/plugins/Link';
import {TVideoLink, TOptionalVideoLink} from '@packages/systems/plugins/Video';
import {TColor, TOptionalColor} from '@packages/systems/plugins/Color';
import {
  pluginName,
  typeName,
  identifier,
  qualify,
  getPluginName,
  getName,
} from '@packages/systems/wfdl-Qualified';
import {NON_CONFIGURABLE_FIELD_SLUGS} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {type CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import type {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {isCommerceCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {weakMemo} from '@packages/utilities/memo';

export const collectionItemCreatorName = (collection: {name: string}) =>
  collection.name;

const dynamoFieldToDataType = (field: any): null | Type => {
  const ty = _dynamoFieldToDataType(field);
  if (!ty) return ty;
  const system = {label: field.name, isFullWidth: true} as const;
  return withSystem(system, ty);
};

const _dynamoFieldToDataType = (field: any): null | Type => {
  const type = field.type;

  if (type === 'PlainText') {
    const singleLine = Map(field.validations).get('singleLine', true);
    const meta = {multiLine: !singleLine} as const;
    return field.required
      ? withMeta(meta, TNonEmptyText)
      : withMeta(meta, TText);
  }
  if (type === 'Number') return field.required ? TNumber : TOptionalNumber;
  if (type === 'Bool') return TBoolean;
  if (type === 'Email') return field.required ? TEmail : TOptionalEmail;
  if (type === 'Phone') return field.required ? TNonEmptyPhone : TPhone;
  if (type === 'Link') return field.required ? TLink : TOptionalLink;
  if (type === 'Video') return field.required ? TVideoLink : TOptionalVideoLink;
  if (type === 'Color') return field.required ? TColor : TOptionalColor;

  if (type === 'Option') {
    const validations = Map(field.validations);
    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
    const options = validations
      .get('options', List())
      // @ts-expect-error - TS7006 - Parameter 'vals' implicitly has an 'any' type. | TS7006 - Parameter 'option' implicitly has an 'any' type.
      .reduce<Record<string, any>>((vals, option) => {
        // @ts-expect-error - TS2538 - Type 'unknown' cannot be used as an index type.
        vals[Map(option).get('name', '')] = TEmptyRecord;
        return vals;
      }, {});
    return withMeta({variant: 'menu'}, TUnion(options));
  }

  // We don’t know how to handle anything else yet.
  return null;
};

const itemStatusTy = withSystem(
  {labelVariant: 'hidden'},
  TTypeConstructor(qualify(pluginName('CMS'), typeName('ItemStatusDropdown')))
);

const basicInfoFieldsTy = withSystem(
  {label: 'basicFields', labelVariant: 'hidden'},
  TTypeConstructor(qualify(pluginName('CMS'), typeName('Basic Info Fields')))
);

const updateItemStatusTy = withSystem(
  {labelVariant: 'hidden'},
  TTypeConstructor(qualify(pluginName('CMS'), typeName('UpdateItemStatus')))
);

const configVar = $('config');

const selectBasicInfo = ESelect({
  from: configVar,
  prop: 'Basic Info Fields',
});

const selectName = ESelect({
  from: selectBasicInfo,
  prop: 'name',
});

const selectSlug = ESelect({
  from: selectBasicInfo,
  prop: 'slug',
});

const selectItemStatus = ESelect({
  from: configVar,
  prop: 'Item Status',
});

const selectCustomFieldsFromConfig = ESelect({
  from: configVar,
  prop: 'Custom Fields',
});

const selectItemIDConfig = ESelect({
  from: configVar,
  prop: 'Item ID',
});

const selectSelectedFieldsConfig = ESelect({
  from: configVar,
  prop: 'Selected Fields',
});

const itemStatusToTextVar = _$('CMS', 'Item status to Text');
const updateItemStatusToTextVar = _$('CMS', 'Update item status to Text');
const itemIDToTextVar = _$('CMS', 'Item ID to Text');

const createItemVar = _$('Effect', 'Low-level create CMS item');
const deleteItemVar = _$('Effect', 'Low-level delete CMS item');
const lookupItemVar = _$('Effect', 'Low-level lookup CMS item');
const listToObjectVar = _$('CMS', 'List to array of objects');
const updateItemVar = _$('Effect', 'Low-level update CMS item');

const jsonFromText = call1(_$('Json', 'From Text'));
const jsonFromNumber = call1(_$('Json', 'From Number'));
const jsonFromBoolean = call1(_$('Json', 'From Boolean'));

const emailToText = call1(_$('Email', 'To Text'));
const linkToText = call1(_$('Link', 'To Text'));
const videoToText = call1(_$('Video', 'To Text'));
const colorToText = call1(_$('Color', 'To Text'));

const fromOption = call2(_$('Option', 'From Option'));

const jsonFromEmail = (value: Expression) => jsonFromText(emailToText(value));
const jsonFromLink = (value: Expression) => jsonFromText(linkToText(value));
const jsonFromVideo = (value: Expression) => jsonFromText(videoToText(value));
const jsonFromColor = (value: Expression) => jsonFromText(colorToText(value));

const maybeGetField = call2(_$('Logic', 'Maybe get field'));

const encodeField =
  (encoder: (arg: Expression) => Expression) =>
  (value: Expression, field: any) =>
    EUnion({
      tag: 'Some',
      value: ERecord({name: EText(field.slug), value: encoder(value)}),
    });

const encodeOptionField =
  (encoder: (arg: Expression) => Expression) =>
  (value: Expression, field: any) =>
    ECase({
      value,
      cases: {
        None: func(['_'], EUnion({tag: 'None', value: ERecord({})})),
        Some: func(
          ['value'],
          EUnion({
            tag: 'Some',
            value: ERecord({
              name: EText(field.slug),
              value: encoder($('value')),
            }),
          })
        ),
      },
    });

const dynamoFieldToJsonEncoder = (
  value: Expression,
  field: any
): null | Expression => {
  const mkExpr = _dynamoFieldToJsonEncoder(field);
  if (!mkExpr) return mkExpr;
  return mkExpr(value, field);
};

const _dynamoFieldToJsonEncoder = (field: any) => {
  const type = field.type;

  if (type === 'PlainText') return encodeField(jsonFromText);
  if (type === 'Number')
    return field.required
      ? encodeField(jsonFromNumber)
      : encodeOptionField(jsonFromNumber);
  if (type === 'Bool') return encodeField(jsonFromBoolean);
  if (type === 'Email')
    return field.required
      ? encodeField(jsonFromEmail)
      : encodeOptionField(jsonFromEmail);
  if (type === 'Phone') return encodeField(jsonFromText);
  if (type === 'Link')
    return field.required
      ? encodeField(jsonFromLink)
      : encodeOptionField(jsonFromLink);
  if (type === 'Video')
    return field.required
      ? encodeField(jsonFromVideo)
      : encodeOptionField(jsonFromVideo);
  if (type === 'Color')
    return field.required
      ? encodeField(jsonFromColor)
      : encodeOptionField(jsonFromColor);

  if (type === 'Option') {
    const validations = Map(field.validations);
    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
    const cases = validations
      .get('options', List())
      // @ts-expect-error - TS7006 - Parameter 'vals' implicitly has an 'any' type. | TS7006 - Parameter 'option' implicitly has an 'any' type.
      .reduce<Record<string, any>>((vals, option) => {
        const name = Map(option).get('name', '');
        // @ts-expect-error - TS2538 - Type 'unknown' cannot be used as an index type.
        vals[name] = EFunction({
          param: '_',
          // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
          body: jsonFromText(EText(name)),
        });
        return vals;
      }, {});
    return encodeField((value) => ECase({value, cases}));
  }

  // We don’t know how to handle anything else yet.
  return null;
};

const parseBooleanFromJson = _$('Effect', 'Parse Boolean from Json');
const parseNumberFromJson = _$('Effect', 'Parse Number from Json');
const parseTextFromJson = _$('Effect', 'Parse Text from Json');
const parseListFromJson = _$('Effect', 'Parse List from Json');
const traverseList = call2(_$('Effect', 'Traverse List'));
const parseNameValuePairsFromJson = _$(
  'Effect',
  'Parse Name & Value pairs from Json'
);

const cmsParseError = (message: Expression) =>
  fail(
    EUnion({
      tag: 'CMS Parse Error',
      value: message,
    })
  );

const outputVar = $('output');
const fieldVar = $('field');
const optionVar = $('option');

const selectCustomFieldsFromOutput = ESelect({
  from: outputVar,
  prop: 'Custom Fields',
});

const invalidCMSOption = cmsParseError(
  concat([
    EText('The value `'),
    optionVar,
    EText('` is not a valid option for the CMS item.'),
  ])
);

const parseTextField = ECall({fun: parseTextFromJson, arg: fieldVar});

const optionTextEquals = (option: string) =>
  textEquals(optionVar, EText(option));

const dynamoFieldToJsonDecoder = (
  field: any
): null | ((arg1: string) => Expression) => {
  const type = field.type;

  if (type === 'PlainText') {
    return decodeTextField;
  }

  if (type === 'Number') {
    return (field.required ? decodeField : decodeOptionField)(
      ECall({fun: parseNumberFromJson, arg: fieldVar})
    );
  }

  if (type === 'Bool') {
    return decodeField(ECall({fun: parseBooleanFromJson, arg: fieldVar}));
  }

  if (type === 'Option') {
    const validations = Map(field.validations);
    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
    const parseOption = validations
      .get('options', List())
      // @ts-expect-error - TS7006 - Parameter 'next' implicitly has an 'any' type. | TS7006 - Parameter 'option' implicitly has an 'any' type.
      .reduce((next, option) => {
        const name = Map(option).get('name', '');
        return ECase({
          // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
          value: optionTextEquals(name),
          cases: {
            True: EFunction({
              param: '_',
              // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string'.
              body: pure(EUnion({tag: name, value: ERecord({})})),
            }),
            False: EFunction({param: '_', body: next}),
          },
        });
      }, invalidCMSOption);

    return decodeField(
      bind(parseTextField, EFunction({param: 'option', body: parseOption}))
    );
  }

  if (type === 'Email') {
    return (field.required ? decodeField : decodeOptionField)(
      bind(parseTextField, expectEmailRef)
    );
  }

  if (type === 'Phone') {
    return decodeField(parseTextField);
  }

  if (type === 'Link') {
    return (field.required ? decodeField : decodeOptionField)(
      bind(parseTextField, expectLinkRef)
    );
  }

  if (type === 'Video') {
    return (field.required ? decodeField : decodeOptionField)(
      bind(parseTextField, expectVideoLinkRef)
    );
  }

  if (type === 'Color') {
    return (field.required ? decodeField : decodeOptionField)(
      bind(parseTextField, expectColorRef)
    );
  }

  // We don’t know how to handle anything else yet.
  return null;
};

const toNameValueRecords = (obj: {[key: string]: never}) =>
  foldRight(
    func(
      ['option', 'pairs'],
      ECase({
        value: $('option'),
        cases: {
          None: func(['_'], $('pairs')),
          Some: func(['pair'], prepend($('pair'), $('pairs'))),
        },
      })
    ),
    EList([]),
    EList(Object.values(obj))
  );

// @ts-expect-error - TS18046 - 'creator' is of type 'unknown'.
const getCreatorFieldTypes = mapValues((creator) => creator.fieldType);
// @ts-expect-error - TS18046 - 'creator' is of type 'unknown'.
const getCreatorFieldEncoders = mapValues((creator) => creator.fieldEncoder);

const decodeField = (decoder: Expression) => (fieldName: string) =>
  bind(
    lookup(EText(fieldName), selectCustomFieldsFromOutput),
    func(['field'], decoder)
  );

const decodeOptionField = (decoder: Expression) => (fieldName: string) =>
  ECase({
    value: maybeGetField(EText(fieldName), selectCustomFieldsFromOutput),
    cases: {
      None: func(['_'], pure(EUnion({tag: 'None', value: ERecord({})}))),
      Some: func(
        ['field'],
        bind(
          decoder,
          func(['value'], pure(EUnion({tag: 'Some', value: $('value')})))
        )
      ),
    },
  });

const decodeTextField = (fieldName: string) =>
  ECase({
    value: maybeGetField(EText(fieldName), selectCustomFieldsFromOutput),
    cases: {
      None: func(['_'], pure(EText(''))),
      Some: func(['field'], parseTextField),
    },
  });

const fieldCreatorsReducer = (
  creators: null | Record<any, any>,
  field: any
) => {
  if (!creators) return creators;

  const slug = field.slug;

  if (
    NON_CONFIGURABLE_FIELD_SLUGS.hasOwnProperty(slug) ||
    slug === 'name' ||
    slug === 'slug'
  ) {
    return creators;
  }

  const fieldType = dynamoFieldToDataType(field);
  const fieldEncoder = dynamoFieldToJsonEncoder(
    ESelect({from: selectCustomFieldsFromConfig, prop: slug}),
    field
  );
  const decoder = dynamoFieldToJsonDecoder(field);
  const fieldDecoder = decoder ? decoder(slug) : null;

  if (fieldType && fieldEncoder && fieldDecoder) {
    creators[slug] = {fieldType, fieldEncoder, fieldDecoder};
    return creators;
  }

  if (!fieldType && !field.required) {
    return creators;
  }

  return null;
};

const parseFields = (creators: Record<any, any>) => {
  const fieldNames = Object.keys(creators);

  const item = ERecord(
    fieldNames.reduce(
      (props, name) => {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ name: ExpressionF<any>; slug: ExpressionF<any>; }'.
        props[name] = $(name);
        return props;
      },
      {
        name: ESelect({from: outputVar, prop: 'Name'}),
        slug: ESelect({from: outputVar, prop: 'Slug'}),
      }
    )
  );

  const buildOutput = pure(
    ERecord({
      Item: item,
      'Item ID': ESelect({from: outputVar, prop: 'Item ID'}),
      'Item URL': ESelect({from: outputVar, prop: 'Item URL'}),
      'Item Status': ESelect({from: outputVar, prop: 'Item Status'}),
      'Created On': ESelect({from: outputVar, prop: 'Created On'}),
      'Created By': ESelect({from: outputVar, prop: 'Created By'}),
    })
  );

  return fieldNames.reduce(
    (next, name) =>
      bind(creators[name].fieldDecoder, EFunction({param: name, body: next})),
    buildOutput
  );
};

type BlockTypes = 'create' | 'update' | 'lookup';
const defaultOutput = {
  'Item ID': TText,
  'Item URL': TText,
  'Created On': TText,
  'Created By': TText,
} as const;

const staticStatusOutput = {
  draft: TEmptyRecord,
  publish: TEmptyRecord,
  staged: TEmptyRecord,
} as const;

const statusUpdateFields = {
  current: TEmptyRecord,
  archived: TEmptyRecord,
} as const;
const itemOutputType = (
  creatorFieldTypes:
    | Record<any, any>
    | {
        [key: string]: Type;
      },
  blockType: BlockTypes
) => {
  return {
    Item: TRecord({
      ...creatorFieldTypes,
      name: TText,
      slug: TText,
    }),
    ...defaultOutput,

    // Item status has to be derived from block type
    'Item Status': TUnion({
      ...(blockType === 'update' && statusUpdateFields),
      ...staticStatusOutput,
    }),
  };
};

type KeyTypeObject = {
  [key: string]: Type;
};

// TODO! We should not need to do this mapping if the names are already capitalized (Name/Value).
// See https://webflow.atlassian.net/browse/LOGIC-4791

const mapNameValuePairs = (param: string) =>
  listMap(
    EFunction({
      param: 'item',
      body: ERecord({
        name: ESelect({from: $('item'), prop: 'Name'}),
        value: ESelect({from: $('item'), prop: 'Value'}),
      }),
    }),
    $(param)
  );

export const makeCollectionFunctionType = (
  values: {
    [key: string]: EnvTypes.TypedValue;
  },
  coll: CollectionRecordType
) => {
  if (isCommerceCollection(coll)) {
    return values;
  }

  const fieldCreators = coll.fields.reduce<Record<string, any>>(
    // @ts-expect-error - TS2345 - Argument of type '(creators: null | Record<any, any>, field: any) => Record<any, any> | null' is not assignable to parameter of type '(reduction?: Record<string, any> | undefined, value?: FieldType | undefined, key?: number | undefined, iter?: Iterable<number, FieldType> | undefined) => Record<...>'.
    fieldCreatorsReducer,
    {}
  );
  const creatorFieldTypes = fieldCreators
    ? getCreatorFieldTypes(fieldCreators)
    : {};

  /**
   * TODO - get the field encoders for Selected Fields
   */
  const allFieldTypes: KeyTypeObject = {
    ...creatorFieldTypes,
    name: TText,
    slug: TText,
  };

  const optionCMSFieldsToTextValues = fieldCreators
    ? coll.fields.reduce<Record<string, any>>((acc, collectionField) => {
        // We only want to add 'To Text' type values for Option-type CMS fields
        if (collectionField.type === 'Option') {
          // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS18048 - 'collectionField' is possibly 'undefined'.
          const options = Map(collectionField.validations)
            .get('options', List())
            // @ts-expect-error - TS7006 - Parameter 'optionsAcc' implicitly has an 'any' type. | TS7006 - Parameter 'option' implicitly has an 'any' type.
            .reduce<Record<string, any>>((optionsAcc, option) => {
              const optionName = Map(option).get('name', '');
              // @ts-expect-error - TS2538 - Type 'unknown' cannot be used as an index type. | TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
              optionsAcc[optionName] = func(['_'], EText(optionName));
              return optionsAcc;
            }, {});

          const typeToAdd = {
            envType: {
              // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
              dataType: tyfun([allFieldTypes[collectionField.slug]], TText),
              exported: 'visible',
              implicit: 'prefilledArgs',
            },

            expression: func(
              [identifier('field option')],
              ECase({
                value: $('field option'),
                cases: options,
              })
            ),
          } as const;
          acc[`${coll.name}: ${collectionField.name} to Text`] = typeToAdd;
        }

        return acc;
      }, {})
    : {};

  return {
    ...values,
    ...optionCMSFieldsToTextValues,

    /**
     * Create CMS item
     */
    // Can only create an item if the collection has 1 or more compatible fields
    ...(fieldCreators && {
      [`Create ${coll.name}`]: {
        envType: {
          dataType: forall(
            ['other errors'],
            functionOf(
              TRecord({
                'Item Status': itemStatusTy,
                'Basic Info Fields': basicInfoFieldsTy,
                ...(Object.keys(creatorFieldTypes).length === 0
                  ? null
                  : {
                      'Custom Fields': withSystem(
                        {labelVariant: 'hidden'},
                        TTypeApplication({
                          con: TTypeConstructor(
                            qualify(
                              pluginName('CMS'),
                              typeName('Custom Fields')
                            )
                          ),
                          arg: TRecord(creatorFieldTypes),
                        })
                      ),
                    }),
              }),
              TEffect(
                TUnion(
                  {
                    'Missing field': TText,
                    'Failed parsing from Json': TJson,
                    'CMS Parse Error': TText,
                  },
                  TTypeReference('other errors')
                ),
                TRecord({
                  ...itemOutputType(creatorFieldTypes, 'create'),
                })
              )
            )
          ),
          exported: 'visible',
        },
        expression: EFunction({
          param: 'config',
          body: bind(
            ECall({
              fun: createItemVar,
              arg: ERecord({
                collectionName: EText(coll.name),
                itemName: selectName,
                itemSlug: selectSlug,
                fields: toNameValueRecords(
                  // @ts-expect-error - TS2345 - Argument of type '{ [key: string]: any; }' is not assignable to parameter of type '{ [key: string]: never; }'.
                  getCreatorFieldEncoders(fieldCreators)
                ),
                status: ECall({
                  fun: itemStatusToTextVar,
                  arg: selectItemStatus,
                }),
              }),
            }),
            EFunction({
              param: 'output',
              body: parseFields(fieldCreators),
            })
          ),
        }),
      },
    }),
    /**
     * Delete CMS item
     */
    [`Delete ${coll.name}`]: {
      envType: {
        dataType: forall(
          ['other errors'],
          functionOf(
            withSystem(
              {isFullWidth: true, label: 'Item ID', labelVariant: 'hidden'},
              TRecord({
                'Item ID': TTypeConstructor(
                  qualify(pluginName('CMS'), typeName('Item ID Dropdown'))
                ),
              })
            ),
            TEffect(
              TUnion(
                {
                  'Missing field': TText,
                },
                TTypeReference('other errors')
              ),
              TRecord({})
            )
          )
        ),
        exported: 'visible',
      },
      expression: EFunction({
        param: 'config',
        body: bind(
          ECall({
            fun: deleteItemVar,
            arg: ERecord({
              collectionName: EText(coll.name),
              itemId: ECall({
                fun: itemIDToTextVar,
                arg: selectItemIDConfig,
              }),
            }),
          }),
          EFunction({
            param: 'output',
            body: pure(ERecord({})),
          })
        ),
      }),
    },
    /**
     * Update CMS item
     */
    // Can only update CMS items if the collection has at least 1 compatible field
    ...(fieldCreators && {
      [`Update ${coll.name}`]: {
        envType: {
          dataType: forall(
            ['other errors'],
            functionOf(
              TRecord({
                'Item ID': TTypeConstructor(
                  qualify(pluginName('CMS'), typeName('Item ID Dropdown'))
                ),
                'Item Status': updateItemStatusTy,
                'Selected Fields': TList(TUnion(allFieldTypes)),
              }),
              TEffect(
                TUnion(
                  {
                    'Missing field': TText,
                    'Failed parsing from Json': TJson,
                    'CMS Parse Error': TText,
                  },
                  TTypeReference('other errors')
                ),
                TRecord({
                  ...itemOutputType(creatorFieldTypes, 'update'),
                })
              )
            )
          ),
          exported: 'visible',
        },
        expression: EFunction({
          param: 'config',
          body: bind(
            ECall({
              fun: updateItemVar,
              arg: ERecord({
                collectionName: EText(coll.name),
                itemId: ECall({
                  fun: itemIDToTextVar,
                  arg: selectItemIDConfig,
                }),
                fields: ECall({
                  fun: listToObjectVar,
                  arg: selectSelectedFieldsConfig,
                }),
                status: ECall({
                  fun: updateItemStatusToTextVar,
                  arg: selectItemStatus,
                }),
              }),
            }),
            EFunction({
              param: 'output',
              body: parseFields(fieldCreators),
            })
          ),
        }),
      },
    }),
    /**
     * CMS item published trigger
     */
    ...(fieldCreators && {
      [`CMS Trigger: ${coll.name} updated`]: {
        envType: {
          dataType: forall(
            ['other errors'],

            functionOf(
              TJson,
              TEffect(
                TUnion(
                  {
                    'Missing field': TText,
                    'Failed parsing from Json': TJson,
                    'CMS Parse Error': TText,
                  },
                  TTypeReference('other errors')
                ),
                TRecord({
                  ...itemOutputType(creatorFieldTypes, 'create'),
                })
              )
            )
          ),
          exported: 'visible',
        },

        expression: EFunction({
          param: 'json',
          body: flow([
            [
              'pairs',
              ECall({
                fun: parseNameValuePairsFromJson,
                arg: $('json'),
              }),
            ],

            [
              'Custom Fields Json',
              lookup(EText('Custom Fields'), mapNameValuePairs('pairs')),
            ],

            [
              'Custom Fields List',
              ECall({fun: parseListFromJson, arg: $('Custom Fields Json')}),
            ],

            [
              'Custom Fields',
              traverseList(
                EFunction({
                  param: 'Item Json',
                  body: flow([
                    [
                      'Item Pairs',
                      ECall({
                        fun: parseNameValuePairsFromJson,
                        arg: $('Item Json'),
                      }),
                    ],
                    [
                      'name Json',
                      lookup(EText('name'), mapNameValuePairs('Item Pairs')),
                    ],
                    [
                      'name',
                      ECall({fun: parseTextFromJson, arg: $('name Json')}),
                    ],
                    [
                      'value',
                      lookup(EText('value'), mapNameValuePairs('Item Pairs')),
                    ],
                    ['_', pure(ERecord({Name: $('name'), Value: $('value')}))],
                  ]),
                }),
                $('Custom Fields List')
              ),
            ],

            [
              'Item ID Json',
              lookup(EText('Item ID'), mapNameValuePairs('pairs')),
            ],

            [
              'Item ID',
              ECall({
                fun: parseTextFromJson,
                arg: $('Item ID Json'),
              }),
            ],

            ['Name Json', lookup(EText('Name'), mapNameValuePairs('pairs'))],

            [
              'Name',
              ECall({
                fun: parseTextFromJson,
                arg: $('Name Json'),
              }),
            ],

            ['Slug Json', lookup(EText('Slug'), mapNameValuePairs('pairs'))],

            [
              'Slug',
              ECall({
                fun: parseTextFromJson,
                arg: $('Slug Json'),
              }),
            ],

            [
              'Item URL Json',
              lookup(EText('Item URL'), mapNameValuePairs('pairs')),
            ],

            [
              'Item URL',
              ECall({
                fun: parseTextFromJson,
                arg: $('Item URL Json'),
              }),
            ],

            [
              'Created On Json',
              lookup(EText('Created On'), mapNameValuePairs('pairs')),
            ],

            [
              'Created On',
              ECall({
                fun: parseTextFromJson,
                arg: $('Created On Json'),
              }),
            ],

            [
              'Created By Json',
              lookup(EText('Created By'), mapNameValuePairs('pairs')),
            ],

            [
              'Created By',
              ECall({
                fun: parseTextFromJson,
                arg: $('Created By Json'),
              }),
            ],

            [
              'Item Status Json',
              lookup(EText('Item Status'), mapNameValuePairs('pairs')),
            ],

            [
              'Item Status pairs',
              ECall({
                fun: parseNameValuePairsFromJson,
                arg: $('Item Status Json'),
              }),
            ],

            [
              'Item Status tag JSON',
              lookup(EText('tag'), mapNameValuePairs('Item Status pairs')),
            ],

            [
              'Item Status tag',
              ECall({
                fun: parseTextFromJson,
                arg: $('Item Status tag JSON'),
              }),
            ],

            [
              'Item Status',
              ECase({
                value: textEquals($('Item Status tag'), EText('draft')),

                cases: {
                  True: EFunction({
                    param: '_',
                    body: pure(
                      EUnion({
                        tag: 'draft',
                        value: ERecord({}),
                      })
                    ),
                  }),
                  False: EFunction({
                    param: '_',
                    body: ECase({
                      value: textEquals($('Item Status tag'), EText('publish')),

                      cases: {
                        True: EFunction({
                          param: '_',
                          body: pure(
                            EUnion({
                              tag: 'publish',
                              value: ERecord({}),
                            })
                          ),
                        }),
                        False: EFunction({
                          param: '_',
                          body: ECase({
                            value: textEquals(
                              $('Item Status tag'),
                              EText('staged')
                            ),

                            cases: {
                              True: EFunction({
                                param: '_',
                                body: pure(
                                  EUnion({
                                    tag: 'staged',
                                    value: ERecord({}),
                                  })
                                ),
                              }),
                              False: EFunction({
                                param: '_',
                                body: fail(
                                  EUnion({
                                    tag: 'CMS Parse Error',
                                    value: EText('Failed to parse Item Status'),
                                  })
                                ),
                              }),
                            },
                          }),
                        }),
                      },
                    }),
                  }),
                },
              }),
            ],

            [
              'output',
              pure(
                ERecord({
                  'Custom Fields': mapNameValuePairs('Custom Fields'),
                  'Item ID': $('Item ID'),
                  Name: $('Name'),
                  Slug: $('Slug'),
                  'Item URL': $('Item URL'),
                  'Created On': $('Created On'),
                  'Created By': $('Created By'),
                  'Item Status': $('Item Status'),
                })
              ),
            ],

            ['_', parseFields(fieldCreators)],
          ]),
        }),
      },
    }),
    /**
     * Lookup CMS item
     */
    ...(fieldCreators && {
      [`Lookup ${coll.name}`]: {
        envType: {
          dataType: forall(
            ['other errors'],
            functionOf(
              withSystem(
                {labelVariant: 'hidden', isFullWidth: true},
                TTypeConstructor(
                  qualify(
                    pluginName('__SitePlugin'),
                    typeName(`Lookup ${coll.name} fields`)
                  )
                )
              ),
              TEffect(
                TUnion(
                  {
                    'Missing field': TText,
                    'Failed parsing from Json': TJson,
                    'CMS Parse Error': TText,
                  },
                  TTypeReference('other errors')
                ),
                TRecord({
                  ...itemOutputType(creatorFieldTypes, 'lookup'),
                })
              )
            )
          ),
          exported: 'visible',
        },

        expression: EFunction({
          param: 'config',
          body: bind(
            ECall({
              fun: lookupItemVar,
              arg: ERecord({
                collectionName: EText(coll.name),
                searchBy: ECase({
                  value: configVar,
                  cases: Object.keys(allFieldTypes).reduce<Record<string, any>>(
                    (acc, key) => {
                      const field = coll.fields.find((f) => f.slug === key);
                      const encodedField = dynamoFieldToJsonEncoder(
                        $('field value'),
                        field
                      );
                      if (encodedField === null) {
                        return acc;
                      }

                      acc[field.slug] = EFunction({
                        param: 'field value',
                        body: fromOption(
                          ERecord({
                            name: EText(key),
                            value: _$('Json', 'Null'),
                          }),
                          encodedField
                        ),
                      });

                      return acc;
                    },
                    {}
                  ),
                }),
              }),
            }),
            EFunction({
              param: 'output',
              body: parseFields(fieldCreators),
            })
          ),
        }),
      },
    }),
  };
};

const removeCustomFields = dissoc('Custom Fields');

const getSitePluginVarName = Variable.match({
  unqualified: (_) => undefined,
  qualified: (name) =>
    getPluginName(name) === '__SitePlugin' ? getName(name) : undefined,
});

const sitePluginVar = (name: string) =>
  EVariable(
    Variable.qualified(qualify(pluginName('__SitePlugin'), identifier(name)))
  );

const updateBlockByName =
  // @ts-expect-error - TS7006 - Parameter 'options' implicitly has an 'any' type.
  (oldName: string, newName: string, options) => (expr: Expression) => {
    const funArg = Expressions.getCall(expr);
    if (!funArg) return expr;
    const {fun: oldFun, arg: oldArg} = funArg;

    const variable = Expressions.getVariable(oldFun);
    if (!variable) return expr;
    const oldVarName = getSitePluginVarName(variable);

    for (const {name, update} of options) {
      if (oldVarName === name(oldName)) {
        const newArg = update(oldArg);
        return oldName !== newName || oldArg !== newArg
          ? ECall({
              fun: oldName === newName ? oldFun : sitePluginVar(name(newName)),
              arg: newArg,
            })
          : expr;
      }
    }

    return expr;
  };

export const updateCollectionUsage = (
  oldCollection: CollectionRecordType,
  newCollection: CollectionRecordType
) =>
  Expressions.everywhere(
    updateBlockByName(oldCollection.name, newCollection.name, [
      {
        // @ts-expect-error - TS7006 - Parameter 'n' implicitly has an 'any' type.
        name: (n) => `Delete ${n}`,
        update: identity,
      },
      {
        // @ts-expect-error - TS7006 - Parameter 'n' implicitly has an 'any' type.
        name: (n) => `Create ${n}`,
        // @ts-expect-error - TS7006 - Parameter 'arg' implicitly has an 'any' type.
        update: (arg) => {
          const blockConfig = Expressions.getRecord(arg);
          if (!blockConfig) return arg;

          const oldBlockCustomFields = blockConfig['Custom Fields'];
          const oldBlockFields = oldBlockCustomFields
            ? Expressions.getRecord(oldBlockCustomFields)
            : undefined;

          const oldSupportedFields = supportedFieldsBySlug(oldCollection);
          const newSupportedFields = supportedFieldsBySlug(newCollection);

          const removedExtra = oldBlockFields
            ? Object.keys(oldSupportedFields).reduce(
                (blockFields, slug) =>
                  newSupportedFields.hasOwnProperty(slug)
                    ? blockFields
                    : dissoc(slug)(blockFields),
                oldBlockFields
              )
            : oldBlockFields;

          const newBlockFields = Object.keys(newSupportedFields).reduce(
            (blockFields, slug) => {
              const oldField = oldSupportedFields[slug];
              const newField = newSupportedFields[slug];
              const oldValue = blockFields ? blockFields[slug] : undefined;
              const newValue =
                oldField && oldValue
                  ? adjustFieldValue(oldField, newField, oldValue)
                  : initializeField(newField);
              return assoc(slug)(newValue)(blockFields || emptyObject);
            },
            removedExtra
          );

          return newBlockFields === oldBlockFields
            ? arg
            : newBlockFields
              ? ERecord({
                  ...blockConfig,
                  'Custom Fields': ERecord(newBlockFields),
                })
              : ERecord(removeCustomFields(blockConfig));
        },
      },
      {
        // @ts-expect-error - TS7006 - Parameter 'n' implicitly has an 'any' type.
        name: (n) => `Update ${n}`,
        // @ts-expect-error - TS7006 - Parameter 'arg' implicitly has an 'any' type.
        update: (arg) => {
          const blockConfig = Expressions.getRecord(arg);
          if (!blockConfig) return arg;
          const selectedFieldsExpr = blockConfig['Selected Fields'];
          if (!selectedFieldsExpr) return arg;

          const oldSelectedFields = Expressions.getList(selectedFieldsExpr);
          if (!oldSelectedFields) return arg;

          const oldSupportedFields = supportedFieldsBySlug(oldCollection);
          const newSupportedFields = supportedFieldsBySlug(newCollection);

          const newSelectedFields = flatMap((item) => {
            // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'ExpressionF<unknown>'.
            const union = Expressions.getUnion(item);
            if (!union) return [item];
            const slug = union.tag;
            const oldValue = union.value;
            const oldField = oldSupportedFields[slug];
            const newField = newSupportedFields[slug];
            if (!newField) return emptyArray;
            const newValue = oldField
              ? adjustFieldValue(oldField, newField, oldValue)
              : initializeField(newField);
            return [
              newValue === oldValue
                ? item
                : EUnion({tag: slug, value: newValue}),
            ];
          })(oldSelectedFields);

          return newSelectedFields === oldSelectedFields
            ? arg
            : ERecord({
                ...blockConfig,
                'Selected Fields': EList(newSelectedFields),
              });
        },
      },
      {
        // @ts-expect-error - TS7006 - Parameter 'n' implicitly has an 'any' type.
        name: (n) => `Lookup ${n}`,
        // @ts-expect-error - TS7006 - Parameter 'arg' implicitly has an 'any' type.
        update: (arg) => {
          const union = Expressions.getUnion(arg);
          if (!union) return arg;
          const oldSupportedFields = supportedFieldsBySlug(oldCollection);
          const newSupportedFields = supportedFieldsBySlug(newCollection);
          const slug = union.tag;
          const oldValue = union.value;
          const oldField = oldSupportedFields[slug];
          const newField = newSupportedFields[slug];
          if (!newField) return EHole();
          const newValue = oldField
            ? adjustFieldValue(oldField, newField, oldValue)
            : EHole();
          return newValue === oldValue
            ? arg
            : EUnion({tag: slug, value: newValue});
        },
      },
    ])
  );

export const makeCollectionAliasTypes = weakMemo(
  (collections: CollectionsType) => {
    return collections.reduce<Record<string, any>>((acc, collection) => {
      if (isCommerceCollection(collection)) {
        return acc;
      }

      const fieldCreators = collection.fields.reduce<Record<string, any>>(
        // @ts-expect-error - TS2345 - Argument of type '(creators: null | Record<any, any>, field: any) => Record<any, any> | null' is not assignable to parameter of type '(reduction?: Record<string, any> | undefined, value?: FieldType | undefined, key?: number | undefined, iter?: Iterable<number, FieldType> | undefined) => Record<...>'.
        fieldCreatorsReducer,
        {}
      );
      const creatorFieldTypes = fieldCreators
        ? getCreatorFieldTypes(fieldCreators)
        : {};

      /**
       * TODO - get the field encoders for Selected Fields
       */
      const allCMSFields = {
        ...creatorFieldTypes,
        name: TText,
        slug: TText,
      } as const;

      acc[`Lookup ${collection.name} fields`] = createSearchByType(
        allCMSFields,
        collection
      );

      return acc;
    }, {});
  }
);
